import { Box } from "@mui/material";
import { useEffect } from "react";

import SlotBetPanel from "../BetPanel/SlotBetPanel";
import SlotInfoPanels from "../InfoPanels/SlotInfoPanels";
import SlotMachine from "../Machine";
import SlotPayTable from "../PayTable/SlotPayTable";
import SlotProfileBoard from "../ProfileBoard/SlotProfileBoard";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import config from "../../../config/config";
import { CONFIRMATION_DELAY } from "../../../constants/const";
import { Messages } from "../../../constants/messages";
import { getGameBankAllowance } from "../../../helper/contractFunctions/MafiaGameBank";
import { dispatchTxAction } from "../../../helper/dispatchTxAction";
import useGameBankBalance from "../../../hook/useGameBankBalance";
import useWallet from "../../../hook/useWallet";
import {
  approveGameCashAction,
  finishBetAction,
  initializeBetAction,
  updateBetDisabled,
  updateBetStatus,
  updateVrfPending,
} from "../../../reducers/slot.slice";
import { toastInfo } from "../../../utils/utils";
import useSlotBetChainLink from "./hooks/useSlotBet";
import useSlotSSE from "./hooks/useSlotSSE";

import { SlotMachineBetStatus } from "../../../constants/enum/slot";
import useStyles from "./index.styles";

const SlotMachineMain = () => {
  const { classes } = useStyles();

  const { account, connectWallet } = useWallet();
  const dispatch = useAppDispatch();
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance, balance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );

  const {
    currentSlotMachineId,
    slotMachines,
    amountPerSpin,
    spinCount,
    betDisabled,
    betStatus,
    betResult,
  } = useAppSelector((state) => state.slot);

  const { isPending, isFulFilled } = useSlotBetChainLink();

  const handleInitializeBet = async () => {
    if (!account) {
      connectWallet();
      return;
    }

    if (!slotMachines[currentSlotMachineId]) return;

    const totalAmount = amountPerSpin * spinCount;

    if (!slotMachines[currentSlotMachineId].isOpened) {
      toastInfo(Messages.SLOT.INITIALIZE_BET.CLOSE_ERROR);
      return;
    }

    if (amountPerSpin < slotMachines[currentSlotMachineId].minBet) {
      toastInfo(Messages.SLOT.INITIALIZE_BET.MIN_BET_ERROR, {
        minBet: slotMachines[currentSlotMachineId].minBet.toString(),
      });
      return;
    }

    if (amountPerSpin > slotMachines[currentSlotMachineId].maxBet) {
      toastInfo(Messages.SLOT.INITIALIZE_BET.MAX_BET_ERROR, {
        maxBet: slotMachines[currentSlotMachineId].maxBet.toString(),
      });
      return;
    }

    if (balance < totalAmount) {
      toastInfo(Messages.ROULETTE.INITIALIZE_BET.LOW_BALANCE);
      return;
    }

    const allowance = await getGameBankAllowance(account, config.slotAddress);

    const betFun = () => {
      dispatchTxAction(
        dispatch,
        initializeBetAction({
          account,
          spinCount,
          amountPerSpin,
          slotMachineId: currentSlotMachineId,
        }),
        () => {
          dispatch(updateVrfPending(true));
          dispatch(updateBetDisabled(true));
          updateBalance(account, signMsg, signature);
        },
        CONFIRMATION_DELAY
      );
    };

    if (totalAmount > allowance) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          cashAmount: 10 ** 18,
          account,
        }),
        () => {
          betFun();
        },
        CONFIRMATION_DELAY * 2
      );
    } else {
      betFun();
    }
  };

  const handleFinishBet = () => {
    dispatchTxAction(
      dispatch,
      finishBetAction({ account, slotMachineId: currentSlotMachineId }),
      () => {
        if (betStatus === SlotMachineBetStatus.REQUESTED)
          dispatch(updateBetStatus(SlotMachineBetStatus.FINISHING));
      }
    );
  };

  useEffect(() => {
    if (isPending && isFulFilled) {
      handleFinishBet();
      dispatch(updateVrfPending(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, isFulFilled]);

  useEffect(() => {
    if (betStatus !== SlotMachineBetStatus.FINISHING) {
      if (!isPending) {
        dispatch(updateBetStatus(SlotMachineBetStatus.NOT_REQUESTED));
      }
      if (isPending && !isFulFilled) {
        dispatch(updateBetStatus(SlotMachineBetStatus.REQUESTED));
      }
    }
  }, [isPending, isFulFilled, betStatus, dispatch]);

  useSlotSSE(account);

  return (
    <>
      <Box className={classes.slotMachine}>
        <SlotMachine account={account} betResult={betResult} />
      </Box>

      <Box className={classes.slotPanels}>
        <Box className={classes.slotColumn}>
          <SlotBetPanel
            finishBet={handleFinishBet}
            initializeBet={handleInitializeBet}
            betDisabled={betDisabled}
            betStatus={betStatus}
            isSpinnable={isPending && isFulFilled}
          />

          <SlotPayTable />
        </Box>

        <Box className={classes.slotColumn}>
          <SlotProfileBoard />

          <SlotInfoPanels />
        </Box>
      </Box>
    </>
  );
};

export default SlotMachineMain;
